import React, {useState} from 'react';
import {HINT_UPDATE} from '../ia_booster/review/hintKinds';
import {useTranslation} from 'react-i18next';
import {FaIcon} from '../../FaIcon';
import {Switch} from '../../buttons/Swtich';
import {FactBadges} from './FactBadges';
import {FACT_TYPE_MODIFIER, VISIBILITY, isSensitive} from './factConstants';
import {docSourceLabelKey} from './docConstants';
// La fecha de la fuente vive en `factDates` desde que el mazo del Recorrido muestra la misma
// procedencia con otro markup: una función, dos superficies.
import {sourceDate} from './factDates';

// Una fila/card de hecho. `mode`:
// - 'queue'   -> candidato PENDING_REVIEW (acciones aprobar / editar / rechazar).
// - 'library' -> hecho APROBADO (editar / compartir / borrar).
// La provenance (cita textual + documento de origen) es colapsable: es el ancla de confianza
// del revisor. `selectable` agrega un checkbox (lote en la cola / fusión en la biblioteca).
// `similar` muestra un hint anti-duplicados si el candidato se parece a un hecho ya aprobado.
// `similarKind` dice de dónde salió el hint: `'update'` cuando el backend determinó que este hecho
// es un CAMBIO de `similar` (mismo sujeto, otro valor) — la lista tiene que decir lo mismo que la
// carta del mazo («Cambió»), o el revisor lee dos cosas distintas del mismo hecho según la vista.
export function FactCard({
    fact, mode, documentsById, busy, selectable, selected, onToggleSelect, similar, similarKind,
    onApprove, onReject, onEdit, onToggleVisibility, onDelete, onConfirmCurrent,
    // «Ver la cita»: abre el crudo del documento de origen, posicionado en la cita. Lo recibe
    // `(doc, fact)`; sin él, la fuente es texto plano (o el link a Drive, si lo hay).
    onOpenSource,
}) {
    const {t} = useTranslation();
    const isQueue = mode === 'queue';
    // En la COLA la evidencia arranca a la vista: es lo único con lo que se decide aprobar, editar
    // o rechazar, y detrás de un click no la encontraba nadie —ni fede, en vivo, durante una demo
    // (card ySvbQST5)—. En la biblioteca sigue plegada: ahí el hecho ya se curó y la lista es larga.
    const [open, setOpen] = useState(isQueue);

    const doc = fact.source_document_id ? (documentsById || {})[fact.source_document_id] : null;
    const hasProvenance = !!fact.source_quote || !!doc;
    const accent = FACT_TYPE_MODIFIER[fact.fact_type] || '-other';
    const docDate = doc ? sourceDate(doc) : null;

    return (
        <div className={`cmem-fact ${mode === 'queue' ? '-pending' : ''} ${mode === 'library' ? '-compact' : ''} ${accent} ${selected ? '-selected' : ''}`}>
            {selectable && (
                <div className="cmem-fact-select">
                    <input type="checkbox" checked={!!selected}
                           aria-label={t('client_memory.action.select')}
                           onChange={() => onToggleSelect(fact)}/>
                </div>
            )}

            <div className="cmem-fact-main">
                <p className="cmem-statement">{fact.statement}</p>
                {/* En la biblioteca la lista ya viene agrupada por tipo → el badge de tipo es
                    redundante con el header del grupo (el acento del borde izquierdo lo comunica); el
                    estado de compartido lo muestra el toggle de acciones, no un badge. */}
                <FactBadges fact={fact} hideType={mode === 'library'}
                            leading={isQueue ? (
                                // El estado, POR FILA y no sólo en el encabezado de la sección: al
                                // scrollear se pierde el título, y en el piloto se revisaron hechos
                                // ya confirmados creyendo que eran los pendientes (card GcSbgD7B).
                                <span className="cmem-badge cmem-unconfirmed">
                                    {t('client_memory.badge.unconfirmed')}
                                </span>
                            ) : null}/>

                {similar && (
                    <div className="cmem-similar-hint">
                        {/* `copy` y no `code-branch`: los íconos se registran a mano en
                            `init_icons_library.js` y el que no está NO renderiza (sin error, sólo
                            un warning en consola). Es el mismo que usa el hint de duplicado de la
                            carta del mazo (`review/ReviewCard.js`) — un hint, un ícono. */}
                        <FaIcon iconName="copy"/>
                        {similarKind === HINT_UPDATE ? (
                            <span>
                                {`${t('ia_booster.deck.update_of')}: “${similar.statement}”`}
                                <span className="cmem-similar-hint-why">
                                    {' '}{t('ia_booster.deck.update_of_hint')}
                                </span>
                            </span>
                        ) : (
                            <span>{`${t('client_memory.similar_hint')}: “${similar.statement}”`}</span>
                        )}
                    </div>
                )}

                {/* La fuente (tipo + fecha) ES el toggle de la cita: un click expande el textual, sin
                    un "Ver fuente" aparte. Cuando hay cita pero no documento, cae al toggle clásico.
                    El verbo va escrito: un caret solo no dice que atrás hay una cita textual, y esa
                    cita es el argumento anti-alucinación que el producto vende. */}
                {doc ? (
                    <button type="button" className="cmem-fact-source" onClick={() => setOpen(!open)}
                            aria-expanded={open}>
                        <FaIcon iconName="file-lines"/>
                        <span className="cmem-fact-source-type">{t(docSourceLabelKey(doc.source))}</span>
                        {docDate && <span className="cmem-fact-source-date">· {docDate}</span>}
                        <span className="cmem-fact-source-verb">
                            {t(open ? 'client_memory.provenance.hide' : 'client_memory.provenance.show')}
                        </span>
                        <FaIcon iconName={open ? 'chevron-up' : 'chevron-down'}
                                className="cmem-fact-source-caret"/>
                    </button>
                ) : hasProvenance && (
                    <button type="button" className="cmem-provenance-toggle" onClick={() => setOpen(!open)}
                            aria-expanded={open}>
                        <FaIcon iconName={open ? 'chevron-up' : 'chevron-down'}/>
                        {t(open ? 'client_memory.provenance.hide' : 'client_memory.provenance.show')}
                    </button>
                )}
                {open && hasProvenance && (
                    <div className="cmem-provenance">
                        {fact.source_quote && (
                            <blockquote className="cmem-quote">{`“${fact.source_quote}”`}</blockquote>
                        )}
                        {doc && (
                            // Solo el título acá (el tipo + fecha ya están en el chip de arriba). Si la
                            // fuente trae link (archivo de Drive), abre el original en una pestaña nueva.
                            doc.web_view_link ? (
                                <a className="cmem-source-doc -link" href={doc.web_view_link}
                                   target="_blank" rel="noopener noreferrer">
                                    <FaIcon iconName="file-lines"/> {doc.title}
                                    <FaIcon iconName="up-right-from-square" className="cmem-source-doc-ext"/>
                                </a>
                            ) : (
                                <span className="cmem-source-doc">
                                    <FaIcon iconName="file-lines"/> {doc.title}
                                </span>
                            )
                        )}
                        {/* La cita, EN el documento: el visor la resalta y lleva hasta ahí. Sólo
                            cuando hay quien lo reciba y el material dejó texto (`has_raw`). El
                            título va en el `aria-label` como texto plano, nunca interpolado en
                            una clave. */}
                        {doc && onOpenSource && doc.has_raw !== false && (
                            <button type="button" className="cmem-source-doc-open"
                                    aria-label={`${t('client_memory.provenance.open_source')}: ${doc.title}`}
                                    onClick={() => onOpenSource(doc, fact)}>
                                {t('client_memory.provenance.see_quote')}
                            </button>
                        )}
                    </div>
                )}
            </div>

            <div className="cmem-fact-actions">
                {mode === 'queue' && (
                    <>
                        {/* Dos familias de color, no tres: naranja (lleno = aprobar, outline =
                            editar) para seguir adelante, rojo sólo para la que descarta. */}
                        <button type="button" className="btn -alt cmem-sm" disabled={busy}
                                onClick={() => onApprove(fact)}>
                            {t('client_memory.action.approve')}
                        </button>
                        <button type="button" className="btn -alt -negative cmem-sm" disabled={busy}
                                onClick={() => onEdit(fact)}>
                            {t('client_memory.action.edit')}
                        </button>
                        <button type="button" className="btn -alt -negative cmem-sm cmem-reject"
                                disabled={busy} onClick={() => onReject(fact)}>
                            {t('client_memory.action.reject')}
                        </button>
                    </>
                )}
                {mode === 'library' && (
                    <>
                        {fact.is_stale && (
                            <button type="button" className="btn -alt cmem-sm" disabled={busy}
                                    onClick={() => onConfirmCurrent(fact)}>
                                {t('client_memory.action.confirm_current')}
                            </button>
                        )}
                        <button type="button" className="btn -alt -negative cmem-sm" disabled={busy}
                                onClick={() => onEdit(fact)}>
                            {t('client_memory.action.edit')}
                        </button>
                        {isSensitive(fact) ? (
                            <span className="cmem-not-shareable">
                                {t('client_memory.visibility.not_shareable')}
                            </span>
                        ) : (
                            <label className="cmem-share-toggle">
                                <Switch
                                    name={`share-${fact.id}`}
                                    checked={fact.visibility === VISIBILITY.SHARED}
                                    onChange={(e) => onToggleVisibility(fact, e.target.checked)}
                                    disabled={busy}
                                    style={{margin: 0}}
                                />
                                <span>{t('client_memory.visibility.share')}</span>
                            </label>
                        )}
                        <button type="button" className="cmem-link-danger" disabled={busy}
                                onClick={() => onDelete(fact)}>
                            {t('client_memory.action.delete')}
                        </button>
                    </>
                )}
            </div>
        </div>
    );
}
